import { useCallback, useEffect, useState, useRef } from 'react'
import { getMaxChatWindows } from '../Webcomponent/utils/common'

export function useChatWindows() {
  // Window ids are normalized to strings. A chat_id can arrive as a number
  // (recent chats) or a string (contact/profile lookup) for the SAME user;
  // keeping mixed types here made indexOf/=== dedupe fail and opened a second
  // window for a user who was already open. Stringifying makes dedupe reliable.
  const [activeChat, setActiveChat] = useState<string | null>(null)
  const [newChat, setNewChat] = useState<string | null>(null)
  const [allOpenChats, setAllOpenChats] = useState<string[]>([])
  const [limit, setLimit] = useState<number>(0)
  const startChatTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const pendingChatRef = useRef<string | null>(null)

  useEffect(() => {
    const updateLimit = () => setLimit(getMaxChatWindows())
    updateLimit()
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', updateLimit)

      return () => window.removeEventListener('resize', updateLimit)
    }
  }, [])

  const startChat = useCallback(
    (threadId: number | string) => {
      const id = String(threadId)
      // If this is the same chat as pending, ignore
      if (pendingChatRef.current === id) {
        return
      }

      // Clear any existing timeout
      if (startChatTimeoutRef.current) {
        clearTimeout(startChatTimeoutRef.current)
      }

      // Set this chat as pending
      pendingChatRef.current = id

      // Use a small timeout to batch rapid calls
      startChatTimeoutRef.current = setTimeout(() => {
        setActiveChat(id)
        setNewChat(id)

        setAllOpenChats((prev) => {
          // Create a new array to avoid mutating the previous state
          const updated = [...prev]
          const safeLimit = limit > 0 ? limit : Infinity

          // Only add if not already present (compared as normalized strings)
          if (updated.indexOf(id) === -1) {
            updated.push(id)
          }

          return updated.slice(-safeLimit)
        })

        // Clear pending after processing
        pendingChatRef.current = null
        startChatTimeoutRef.current = null
      }, 10) // Small timeout to batch rapid calls
    },
    [limit],
  )

  const closeChat = useCallback((threadId: number | string) => {
    const id = String(threadId)
    // Clear pending if we're closing the pending chat
    if (pendingChatRef.current === id) {
      pendingChatRef.current = null
    }

    // Clear timeout if we're closing the pending chat
    if (startChatTimeoutRef.current) {
      clearTimeout(startChatTimeoutRef.current)
      startChatTimeoutRef.current = null
    }

    setAllOpenChats((prev: string[]) => {
      const updated = prev.filter((cid) => cid !== id)
      //setActiveChat((current) => (current === id ? (updated.at(-1) ?? null) : current))
      setActiveChat((current) => {
        return current === id ? (updated[updated.length - 1] ?? null) : current
      })
      return updated
    })
  }, [])

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (startChatTimeoutRef.current) {
        clearTimeout(startChatTimeoutRef.current)
      }
    }
  }, [])

  return {
    activeChat,
    allOpenChats,
    startChat,
    closeChat,
    setActiveChat,
    newChat,
  }
}
